/**
 * Payment Modal — the ONE payment surface (top-up, zero-balance paywall, gift cards).
 *
 * Driven by the pure reducer in paymentMachine.ts; every sentence comes from
 * messages.ts. Opens on the window event 'lp:open-top-up' (the nav Top Up
 * button, the transcription store's NO_BALANCE, and a zero-balance profile
 * all dispatch it). Pro purchase is disabled until a real month-to-month
 * subscription ships.
 */

import { useState, useCallback, useEffect, useReducer, useRef } from 'react';
import { useProfile, useAuthStore } from '../../store/useAuthStore';
import { supabase } from '../../lib/supabase';
import { paygFloorPriceNote, paygGiftCardNote, PAYG_RATE_TEXT } from '../../services/paygFloor';
import { PAYG_RATE_PER_HOUR_ZAR } from '../../../lib/paygPacks';
import { usePayFastWindowOpen } from './usePayFastWindowOpen';
import { trackCheckoutEvent, trackCouponResult, trackCouponError } from './checkoutTelemetry';
import { buildGenerateBody } from './buildGenerateBody';
import {
  startOnsitePayment,
  outcomeToUi,
  scheduleFollowUps,
  isPayFastWindowOpen,
  loadPayFastScript,
  PAYFAST_ALREADY_OPEN_MESSAGE,
  type FollowUpsHandle,
  type OnsiteOutcome,
  type PaymentPlan,
} from './onsitePayment';
import {
  paymentReducer,
  view,
  INITIAL_PAYMENT_STATE,
  payFastMayBeOpen,
  ONSITE_CALLBACK_TIMEOUT_MS,
  type PaymentAction,
  type PaymentState,
} from './paymentMachine';
import { ACTION_LABELS, BALANCE_UPDATED, CREDIT_PENDING, EARLIER_PAYMENT_ALSO_PAID, PROCESSING, SUPPORT_MAILTO } from './messages';

interface DiscountInfo {
  discount_pct?: number;
  discount_zar?: number;
  description?: string | null;
  effectiveAmount: number;
}

function applyDiscount(base: number, info: DiscountInfo): number {
  if (info.discount_pct) return Math.max(1, parseFloat((base * (1 - info.discount_pct / 100)).toFixed(2)));
  if (info.discount_zar) return Math.max(1, parseFloat((base - info.discount_zar).toFixed(2)));
  return base;
}

const PAYG_OPTIONS = [
  { amount: 50, label: 'R50', time: '6h 40m' },
  { amount: 100, label: 'R100', time: '13h 20m' },
  { amount: 200, label: 'R200', time: '26h 40m' },
] as const;

// Hidden until lp-checkout-repair A4 enables it: discounted payments are
// refused at generate and the ITN credits the discounted amount, so a
// coupon gives the customer nothing today.
const COUPONS_AT_CHECKOUT = false;

/** After a payment the ITN may lag: poll the balance with backoff, then stop. */
const BALANCE_POLL_DELAYS_MS = [2000, 5000, 10000, 20000, 30000, 45000, 60000, 90000, 110000];
const BALANCE_POLL_END_MS = 120000;

/** Bound on asking our own server for a payment id. */
const GENERATE_TIMEOUT_MS = 30_000;

/** Seconds of transcription a rand buys: R7.50/hour (lib/paygPacks.ts PAYG_RATE_PER_HOUR_ZAR). */
const PAYG_SECONDS_PER_RAND = 3600 / PAYG_RATE_PER_HOUR_ZAR;


/** Where the balance at hand-over to PayFast is kept, for a redirect return that reloads the page. */
const BASELINE_KEY = 'lp:payment-baseline';
const BASELINE_MAX_AGE_MS = 2 * 60 * 60 * 1000;

// Storage may be missing or throw (private mode, quota): checkout never depends on it.
function storeBaseline(balance: number | null) {
  if (balance === null) return;
  try {
    window.sessionStorage.setItem(BASELINE_KEY, JSON.stringify({ balance, at: Date.now() }));
  } catch {
    /* ignore */
  }
}

function clearBaseline() {
  try {
    window.sessionStorage.removeItem(BASELINE_KEY);
  } catch {
    /* ignore */
  }
}

/** The stored baseline if it is younger than two hours, else null. The key is removed either way. */
function takeBaseline(): number | null {
  try {
    const raw = window.sessionStorage.getItem(BASELINE_KEY);
    clearBaseline();
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { balance, at } = parsed as { balance?: unknown; at?: unknown };
    if (typeof balance !== 'number' || !Number.isFinite(balance) || typeof at !== 'number') return null;
    const age = Date.now() - at;
    return age >= 0 && age < BASELINE_MAX_AGE_MS ? balance : null;
  } catch {
    clearBaseline();
    return null;
  }
}

function ActionButton({ action, onRetry, onClose }: { action: PaymentAction; onRetry: () => void; onClose: () => void }) {
  const label = ACTION_LABELS[action];
  if (action === 'contact_support') {
    return (
      <a
        href={SUPPORT_MAILTO}
        className="flex-1 text-center py-2 px-3 text-sm rounded-lg bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200 transition-colors"
      >
        {label}
      </a>
    );
  }
  const primary = action === 'retry';
  return (
    <button
      type="button"
      onClick={action === 'retry' ? onRetry : onClose}
      className={`flex-1 py-2 px-3 text-sm rounded-lg transition-colors ${
        primary
          ? 'bg-gray-900 text-white font-semibold hover:bg-gray-800'
          : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
      }`}
    >
      {label}
    </button>
  );
}

/** `initialState` only seeds the reducer (used by tests to render a given state). */
export default function PaymentModal({ initialState = INITIAL_PAYMENT_STATE }: { initialState?: PaymentState } = {}) {
  const profile = useProfile();
  const refreshBalance = useAuthStore((s) => s.refreshBalance);
  const payfastOpen = usePayFastWindowOpen();

  const [state, dispatch] = useReducer(paymentReducer, initialState);
  const [open, setOpen] = useState(false);
  const [selectedPayg, setSelectedPayg] = useState(100);
  // Balance when the attempt OPENED (before anything could credit it); null
  // until a profile is known. Success is only claimed once the balance rises above it.
  const [baseline, setBaseline] = useState<number | null>(null);
  // The balance poll ran its whole window without the balance rising.
  const [creditWindowEnded, setCreditWindowEnded] = useState(false);
  // PayFast sent the customer back with ?payment=success; holds the baseline
  // stored before the redirect (null: none) while waiting for a profile.
  const [returnWatch, setReturnWatch] = useState<{ baseline: number | null } | null>(null);
  // The return had no usable baseline: no rise can be checked, so say only that the balance was updated.
  const [returnUnbaselined, setReturnUnbaselined] = useState(false);

  const [discountCode, setDiscountCode] = useState('');
  const [discountInfo, setDiscountInfo] = useState<DiscountInfo | null>(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountError, setDiscountError] = useState<string | null>(null);

  // Tracks the running onsite payment attempt so we can stop its timers both
  // when the component unmounts and before starting a new attempt — a stray
  // timer must never dispatch into an unmounted component.
  const onsiteStopRef = useRef<(() => void) | null>(null);
  const followUpsRef = useRef<FollowUpsHandle | null>(null);
  // Bumped on every start and on close; an async step that finds it changed
  // belongs to an abandoned attempt and must stop.
  const attemptRef = useRef(0);
  // Aborts the generate request of the running attempt.
  const abortRef = useRef<AbortController | null>(null);
  // The running attempt's generate-request timeout.
  // Balance when the oldest still-unresolved attempt opened; a rise above it proves that payment landed.
  const unresolvedBaselineRef = useRef<number | null>(null);
  const generateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The latest reducer state, for callbacks that outlive the render they were made in.
  const stateRef = useRef<PaymentState>(state);
  // True once startOnsitePayment has been called for the running attempt.
  const payfastStartedRef = useRef(false);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const lastRequestRef = useRef<{ plan: PaymentPlan; amount: number } | null>(null);
  const returnParamRef = useRef<string | null | undefined>(undefined);
  const autoOpenedRef = useRef(false);
  // Checkout telemetry: whether the overlay is showing, and whether the next open is the automatic zero-balance paywall.
  const visibleRef = useRef(false);
  const zeroBalanceOpenRef = useRef(false);
  const statusRef = useRef<HTMLDivElement | null>(null);
  // True once PayFast's window has been seen for the running attempt.
  const sawWindowRef = useRef(false);

  const balance = profile ? profile.balance_seconds : null;
  const visible = open || state.status !== 'idle';
  const inFlight =
    state.status === 'generating' || state.status === 'engine_loading' || state.status === 'awaiting_payfast';

  useEffect(() => {
    visibleRef.current = visible;
  }, [visible]);

  // Nothing may run after unmount: an async step still in flight belongs to an
  // abandoned attempt (the bump), and no timer or interval may outlive us.
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // PayFast's callback can stay silent: a balance that rose above the
  // unresolved attempt's baseline clears its warning all the same.
  useEffect(() => {
    const base = unresolvedBaselineRef.current;
    if (!state.unresolvedAttempt || base === null || balance === null || balance <= base) return;
    dispatch({ type: 'PRIOR_ATTEMPT_RESOLVED', paid: false });
  }, [state.unresolvedAttempt, state.status, balance]);

  useEffect(() => {
    return () => {
      attemptRef.current += 1;
      abortRef.current?.abort();
      if (generateTimerRef.current) clearTimeout(generateTimerRef.current);
      onsiteStopRef.current?.();
      onsiteStopRef.current = null;
      followUpsRef.current?.cancelAll();
    };
  }, []);

  // The ONE open trigger.
  useEffect(() => {
    const handler = () => {
      if (!visibleRef.current) {
        visibleRef.current = true;
        // The ?payment= return is not an open: its own event is sent by the return effect.
        const payment = new URLSearchParams(window.location.search).get('payment');
        if (payment !== 'success' && payment !== 'cancelled') {
          trackCheckoutEvent('modal_opened', zeroBalanceOpenRef.current ? { detail: 'zero_balance' } : {});
        }
      }
      zeroBalanceOpenRef.current = false;
      setOpen(true);
    };
    window.addEventListener('lp:open-top-up', handler);
    return () => window.removeEventListener('lp:open-top-up', handler);
  }, []);

  // A signed-in customer with nothing left meets the paywall once per visit —
  // through the same event, so there is still one way in.
  useEffect(() => {
    if (autoOpenedRef.current || !profile) return;
    if (profile.plan === 'pro' || profile.plan === 'team' || profile.plan === 'enterprise') return;
    if (profile.balance_seconds > 0) return;
    autoOpenedRef.current = true;
    zeroBalanceOpenRef.current = true;
    window.dispatchEvent(new CustomEvent('lp:open-top-up'));
  }, [profile]);

  const pollBalance = useCallback(() => {
    followUpsRef.current?.cancelAll();
    setCreditWindowEnded(false);
    followUpsRef.current = scheduleFollowUps({
      refresh: refreshBalance,
      close: () => setCreditWindowEnded(true),
      closeAfterMs: BALANCE_POLL_END_MS,
      delays: BALANCE_POLL_DELAYS_MS,
    });
  }, [refreshBalance]);

  // ?payment=success|cancelled is PayFast's redirect back to us. Read once and
  // strip it; the ref is cleared after first use so nothing re-dispatches. The
  // credit watch itself is state-driven (below), so a StrictMode re-run of this
  // effect cannot lose it.
  useEffect(() => {
    if (returnParamRef.current === undefined) {
      const params = new URLSearchParams(window.location.search);
      const found = params.get('payment');
      returnParamRef.current = found === 'success' || found === 'cancelled' ? found : null;
      if (returnParamRef.current) {
        params.delete('payment');
        const rest = params.toString();
        window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : '') + window.location.hash);
      }
    }
    const payment = returnParamRef.current;
    if (payment !== 'success' && payment !== 'cancelled') return;
    returnParamRef.current = null;
    setOpen(true);
    dispatch({ type: 'RETURN_QUERY', payment });
    if (payment === 'cancelled') trackCheckoutEvent('cancelled_return');
    else trackCheckoutEvent('callback_true', { detail: 'return' });
    // The ITN has usually credited by now, so a baseline read here would hide the rise.
    const stored = takeBaseline();
    if (payment === 'success') {
      setReturnUnbaselined(stored === null);
      setReturnWatch({ baseline: stored });
    }
  }, []);

  // Return path: use the baseline stored before the redirect once the profile
  // has loaded (never assume 0), then start polling. With no profile the
  // window still ends. With no stored baseline there is nothing to wait for.
  useEffect(() => {
    if (!returnWatch) return;
    if (returnWatch.baseline === null) {
      setReturnWatch(null);
      pollBalance();
      return;
    }
    if (!profile) {
      const id = setTimeout(() => setCreditWindowEnded(true), BALANCE_POLL_END_MS);
      return () => clearTimeout(id);
    }
    setBaseline(returnWatch.baseline);
    setReturnWatch(null);
    pollBalance();
  }, [returnWatch, profile, pollBalance]);

  // An attempt that opened before the profile loaded takes its baseline from
  // the first profile we see.
  useEffect(() => {
    if (state.status === 'success' && baseline === null && profile && !returnUnbaselined && !returnWatch) {
      setBaseline(profile.balance_seconds);
    }
  }, [state.status, baseline, profile, returnUnbaselined, returnWatch]);

  useEffect(() => {
    if (payfastOpen) sawWindowRef.current = true;
  }, [payfastOpen]);

  // ONE 100 s bound, armed on entering `generating` and
  // cleared on every exit from the three in-flight states, so it covers the
  // session refresh, the generate request and the PayFast script load as well
  // as the wait for the callback. Before PayFast was asked to open, nothing
  // was sent to it: the attempt is abandoned and ends in an error. After, the
  // outcome is unknown (timed_out) — unless PayFast's window is open, where the
  // customer may still be typing card details, so the bound does not cut in.
  useEffect(() => {
    if (!inFlight) return;
    const id = setTimeout(() => {
      if (!payfastStartedRef.current) {
        attemptRef.current += 1;
        abortRef.current?.abort();
        trackCheckoutEvent('timeout', { detail: 'before_payfast' });
        dispatch({ type: 'GENERATE_ERR', status: 0 });
        return;
      }
      dispatch({ type: 'ENGINE_READY' });
      if (!isPayFastWindowOpen()) {
        trackCheckoutEvent('timeout', { detail: 'after_payfast' });
        dispatch({ type: 'TIMEOUT' });
      }
    }, ONSITE_CALLBACK_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [inFlight]);

  // Focus moves into the dialog when it opens and goes back to whatever opened
  // it when it closes. Declared before the status-focus effect so an outcome's
  // status box wins on first paint.
  useEffect(() => {
    if (!visible) return;
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialogRef.current?.focus();
    return () => {
      openerRef.current?.focus?.();
      openerRef.current = null;
    };
  }, [visible]);

  // Move focus to the status box after an outcome so screen readers announce
  // it and keyboard users land back in our window.
  useEffect(() => {
    if (state.status !== 'idle') statusRef.current?.focus();
  }, [state.status]);

  const close = useCallback(() => {
    // PayFast's window is on top; closing ours underneath would strand it.
    if (isPayFastWindowOpen()) return;
    attemptRef.current += 1;
    abortRef.current?.abort();
    // The onsite listener is left running on purpose: a paid callback that
    // arrives after the customer closed us must still reach pollBalance.
    setBaseline(null);
    setReturnUnbaselined(false);
    setCreditWindowEnded(false);
    setDiscountCode('');
    setDiscountInfo(null);
    setDiscountError(null);
    dispatch({ type: 'CLOSE' });
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible, close]);

  function onDialogKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'Tab') return;
    const root = dialogRef.current;
    if (!root) return;
    const items = Array.from(
      root.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'),
    );
    if (items.length === 0) {
      e.preventDefault();
      root.focus();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (!root.contains(active)) {
      e.preventDefault();
      first.focus();
    } else if (e.shiftKey && (active === first || active === root || active === statusRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function onBackdropClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) close();
  }

  // Recalculate effective amount whenever selectedPayg changes with an active discount
  useEffect(() => {
    if (discountInfo) {
      setDiscountInfo((prev) => (prev ? { ...prev, effectiveAmount: applyDiscount(selectedPayg, prev) } : null));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPayg]);

  async function validateDiscount() {
    const code = discountCode.trim().toUpperCase();
    if (!code) return;
    setDiscountLoading(true);
    setDiscountError(null);
    setDiscountInfo(null);
    try {
      const res = await fetch(`/api/profile/coupon-validate?code=${encodeURIComponent(code)}`);
      const data = await res.json();
      const result = trackCouponResult(data);
      if (result === 'expired' || result === 'invalid') {
        setDiscountError(result === 'expired' ? 'This discount code has expired.' : 'Invalid or already-used discount code.');
        return;
      }
      if (result === 'not_discount') {
        setDiscountError('That code is not a discount code. Use it in your Wallet to redeem credit.');
        return;
      }
      setDiscountInfo({ ...data, effectiveAmount: applyDiscount(selectedPayg, data) });
    } catch {
      trackCouponError();
      setDiscountError('Could not validate code. Please try again.');
    } finally {
      setDiscountLoading(false);
    }
  }

  const handlePayment = useCallback(
    async (plan: PaymentPlan, amount: number) => {
      // PayFast keeps one global finish callback — never create a payment id
      // (or stop the running attempt) while its window is still open. This is
      // the first thing we do: a refused start must leave the live attempt
      // untouched (its paid callback must still reach the screen), and our
      // overlay is hidden while the window is open, so it is a safety net.
      if (isPayFastWindowOpen()) return;

      lastRequestRef.current = { plan, amount };
      const packId = plan === 'payg' ? `payg_r${amount}` : undefined;
      const attempt = (attemptRef.current += 1);
      const abandoned = () => attempt !== attemptRef.current;
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      // A hung request must end in an error with a retry, not a spinner.
      const generateTimer = setTimeout(() => controller.abort(), GENERATE_TIMEOUT_MS);
      generateTimerRef.current = generateTimer;
      payfastStartedRef.current = false;

      // A retry must never cancel a paid attempt's balance refreshes.
      followUpsRef.current?.cancelClose();
      // The baseline is taken now, before anything can credit the balance.
      const openBaseline = useAuthStore.getState().profile?.balance_seconds ?? null;
      if (!stateRef.current.unresolvedAttempt) unresolvedBaselineRef.current = openBaseline;
      setBaseline(openBaseline);
      setReturnUnbaselined(false);
      setCreditWindowEnded(false);
      sawWindowRef.current = false;
      dispatch({ type: 'OPEN', plan, amount });

      try {
        let session = useAuthStore.getState().session;
        if (!session?.access_token) {
          dispatch({ type: 'GENERATE_ERR', status: 401 });
          return;
        }

        // C7: Proactively refresh if token expires within 60 seconds
        const expiresAt = session.expires_at ?? 0; // Unix seconds
        if (expiresAt - Math.floor(Date.now() / 1000) < 60) {
          const { data: refreshed, error: refreshErr } = await supabase.auth.refreshSession();
          if (abandoned()) return;
          if (refreshErr || !refreshed.session) {
            dispatch({ type: 'GENERATE_ERR', status: 401 });
            return;
          }
          session = refreshed.session;
        }

        const body = buildGenerateBody(
          plan,
          amount,
          plan === 'payg' && discountInfo ? discountCode.trim().toUpperCase() : undefined,
        );

        const res = await fetch('/api/payfast?action=generate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify(body),
          signal: controller.signal,
        });
        if (abandoned()) return;

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          if (abandoned()) return;
          if (res.status === 429) trackCheckoutEvent('rate_limited', { pack_id: packId });
          const header = Number(res.headers.get('Retry-After'));
          dispatch({
            type: 'GENERATE_ERR',
            status: res.status,
            retryAfter: typeof data.retryAfter === 'number' ? data.retryAfter : Number.isFinite(header) && header > 0 ? header : undefined,
            message: typeof data.error === 'string' ? data.error : undefined,
          });
          return;
        }

        const { uuid } = await res.json();
        if (abandoned()) return;
        if (!uuid) {
          dispatch({ type: 'GENERATE_ERR', status: 502 });
          return;
        }
        dispatch({ type: 'GENERATE_OK', uuid, amount });

        await loadPayFastScript();
        if (abandoned()) return;

        // Open the PayFast onsite modal. The callback always wins, even after
        // our own bounded timeout has surfaced a retry message — a customer
        // who finishes paying after 100s must still see success.
        onsiteStopRef.current?.();
        // The redirect return reloads the page: keep the pre-payment balance for it.
        storeBaseline(openBaseline);
        payfastStartedRef.current = true;
        onsiteStopRef.current = startOnsitePayment({
          uuid,
          timeoutMs: ONSITE_CALLBACK_TIMEOUT_MS,
          onWindowOpen: () => {
            sawWindowRef.current = true;
          },
          onOutcome: (outcome: OnsiteOutcome) => {
            // The copy promises the balance updates on its own, so refreshes
            // keep firing whatever the outcome — even a superseded attempt's.
            pollBalance();
            // A superseded attempt must not rewrite the new attempt's screen —
            // but its real callback is never dropped: the refresh above has
            // run, and the warning is cleared. A paid one is acted on: the
            // customer must not start (or finish) a second payment unknowingly.
            if (abandoned()) {
              if (outcome === 'incomplete') return;
              trackCheckoutEvent(outcome === 'paid' ? 'callback_true' : 'callback_false', {
                pack_id: packId,
                payfast_uuid: uuid,
                detail: 'superseded',
              });
              if (outcome !== 'paid') {
                dispatch({ type: 'PRIOR_ATTEMPT_RESOLVED', paid: false });
                return;
              }
              if (!payFastMayBeOpen(stateRef.current)) {
                // The newer attempt has not reached PayFast: stop it, and show
                // this attempt's payment against ITS baseline and amount.
                attemptRef.current += 1;
                abortRef.current?.abort();
                if (generateTimerRef.current) clearTimeout(generateTimerRef.current);
                setBaseline(openBaseline);
                setCreditWindowEnded(false);
                setOpen(true);
              }
              dispatch({ type: 'PRIOR_ATTEMPT_RESOLVED', paid: true, plan, amount });
              return;
            }
            // This attempt's own real callback: no redirect return will follow.
            if (outcome !== 'incomplete') clearBaseline();
            const ui = outcomeToUi(outcome, plan);
            // 'incomplete' is our own guess (window gone or silent); only a
            // real PayFast callback says paid or cancelled.
            if (outcome === 'incomplete') {
              if (sawWindowRef.current) {
                trackCheckoutEvent('callback_false', { pack_id: packId, payfast_uuid: uuid, detail: 'window_closed_guess' });
              } else {
                trackCheckoutEvent('timeout', { pack_id: packId, payfast_uuid: uuid, detail: 'no_window' });
              }
              dispatch({ type: sawWindowRef.current ? 'PAYFAST_CANCEL' : 'TIMEOUT' });
            } else {
              trackCheckoutEvent(ui.success ? 'callback_true' : 'callback_false', { pack_id: packId, payfast_uuid: uuid });
              dispatch({ type: 'CALLBACK', result: ui.success });
            }
          },
        });
        // Only once PayFast has actually been asked to open: if the call
        // threw, we are still engine_loading and GENERATE_ERR applies.
        dispatch({ type: 'ENGINE_READY' });
        trackCheckoutEvent('engine_loaded', { pack_id: packId, payfast_uuid: uuid });
      } catch (err) {
        if (abandoned()) return;
        console.error('[PaymentModal] could not start payment', err);
        dispatch({
          type: 'GENERATE_ERR',
          status: 0,
          message: err instanceof Error && err.message === PAYFAST_ALREADY_OPEN_MESSAGE ? PAYFAST_ALREADY_OPEN_MESSAGE : undefined,
        });
      } finally {
        clearTimeout(generateTimer);
      }
    },
    [discountInfo, discountCode, pollBalance]
  );

  function onRetry() {
    const last = lastRequestRef.current;
    if (last) void handlePayment(last.plan, last.amount);
    else dispatch({ type: 'RESET' });
  }

  if (!visible) return null;

  const v = view(state);
  // "Credit has been added" is only ever said once the balance actually rose
  // above the baseline taken when the attempt opened. A gift credits a code,
  // not the balance, so there is nothing to wait for.
  const expectsCredit = state.status === 'success' && state.plan !== 'gift';
  const credited = baseline !== null && balance !== null && balance > baseline;
  // The pack amount is only named when the rise covers it: another attempt's
  // late credit, or a redemption elsewhere, can raise the balance by less.
  const coversPack =
    credited && state.amount !== null && balance - baseline >= state.amount * PAYG_SECONDS_PER_RAND;
  const unbaselined = returnUnbaselined && state.status === 'success';
  const waitingForCredit = expectsCredit && !unbaselined && !credited && !creditWindowEnded;
  const creditPending = expectsCredit && !unbaselined && !credited && creditWindowEnded;
  const creditMessage = waitingForCredit
    ? PROCESSING
    : creditPending
      ? CREDIT_PENDING
      : expectsCredit && (unbaselined || !coversPack)
        ? BALANCE_UPDATED
        : null;
  // view() already leads with the duplicate-payment line; a credit sentence that replaces it must too.
  const message =
    creditMessage === null ? v.message : (state.earlierPaid ? `${EARLIER_PAYMENT_ALSO_PAID} ` : '') + creditMessage;
  const isAlert = state.status === 'error' || state.status === 'rate_limited';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      style={{ display: payfastOpen ? 'none' : undefined }}
      aria-hidden={payfastOpen || undefined}
      data-testid="payment-overlay"
      onClick={onBackdropClick}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="payment-modal-title"
        ref={dialogRef}
        tabIndex={-1}
        onKeyDown={onDialogKeyDown}
        className="focus:outline-none bg-white border border-gray-200 rounded-2xl shadow-2xl max-w-lg w-full p-8 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex justify-between items-center mb-6">
          <h2 id="payment-modal-title" className="text-xl font-bold text-gray-900">Top Up Balance</h2>
          <button type="button" aria-label="Close payment modal" onClick={close} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        {state.status !== 'idle' ? (
          <div>
            <div
              ref={statusRef}
              tabIndex={-1}
              role={isAlert ? 'alert' : 'status'}
              className={`mb-4 p-4 rounded-lg text-sm flex items-start gap-3 ${
                state.status === 'success' && !waitingForCredit && !creditPending
                  ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                  : isAlert
                    ? 'bg-red-50 border border-red-200 text-red-700'
                    : 'bg-amber-50 border border-amber-200 text-amber-800'
              }`}
            >
              {(inFlight || waitingForCredit) && (
                <svg className="w-5 h-5 flex-shrink-0 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
              )}
              <span>{message}</span>
            </div>
            <div className="flex gap-2">
              {v.actions.map((action) => (
                <ActionButton key={action} action={action} onRetry={onRetry} onClose={close} />
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              {/* PAYG */}
              <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                <h3 className="font-semibold text-gray-900">Pay-As-You-Go</h3>
                <p className="text-2xl font-bold text-gray-900 mt-2">
                  {PAYG_RATE_TEXT}<span className="text-sm font-normal text-gray-400">/hour</span>
                </p>
                <p className="text-xs text-gray-500 mt-1">{paygFloorPriceNote()}</p>
                <p className="text-xs text-gray-400 mt-1">Choose your top-up amount</p>
                <div className="flex gap-1.5 mt-3">
                  {PAYG_OPTIONS.map((opt) => (
                    <button key={opt.amount} type="button" onClick={() => setSelectedPayg(opt.amount)}
                      className={`flex-1 py-1.5 text-xs rounded-md font-medium transition-colors ${
                        selectedPayg === opt.amount
                          ? 'bg-[#00d4b8] text-[#0d1117]'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >{opt.label}</button>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-1.5 text-center">
                  {PAYG_OPTIONS.find((o) => o.amount === selectedPayg)?.time} of transcription
                </p>

                {/* Discount code — hidden until lp-checkout-repair A4 fixes coupons end to end */}
                {COUPONS_AT_CHECKOUT && (
                  <>
                    <div className="mt-3 flex gap-1.5">
                      <input
                        type="text"
                        value={discountCode}
                        onChange={e => { setDiscountCode(e.target.value.toUpperCase()); setDiscountInfo(null); setDiscountError(null); }}
                        placeholder="Discount code"
                        maxLength={32}
                        className="flex-1 bg-white border border-gray-300 rounded-md px-2 py-1.5 text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#00d4b8] font-mono uppercase"
                      />
                      <button
                        type="button"
                        onClick={validateDiscount}
                        disabled={discountLoading || !discountCode.trim()}
                        className="px-3 py-1.5 text-xs bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 disabled:opacity-50 transition-colors"
                      >
                        {discountLoading ? '…' : 'Apply'}
                      </button>
                    </div>
                    {discountError && <p className="mt-1 text-xs text-red-600">{discountError}</p>}
                    {discountInfo && (
                      <p className="mt-1 text-xs text-[#007a6d]">
                        ✓ Discount applied — Pay R{discountInfo.effectiveAmount.toFixed(2)} <span className="text-gray-400">(was R{selectedPayg})</span>
                        {discountInfo.description && <span className="text-gray-500"> · {discountInfo.description}</span>}
                      </p>
                    )}
                  </>
                )}

                <button type="button" onClick={() => handlePayment('payg', selectedPayg)}
                  className="mt-3 w-full py-2 px-3 text-sm bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors"
                >
                  {discountInfo ? `Pay R${discountInfo.effectiveAmount.toFixed(2)}` : `Pay R${selectedPayg}`}
                </button>
              </div>

              {/* Pro */}
              <div className="border-2 border-[#00d4b8] rounded-xl p-4 relative bg-[#00d4b8]/5">
                <h3 className="font-semibold text-gray-900">Pro</h3>
                <p className="text-2xl font-bold text-gray-900 mt-2">
                  R299<span className="text-sm font-normal text-gray-400">/month</span>
                </p>
                <p className="text-xs text-gray-400 mt-1">Unlimited transcription</p>
                <button type="button" disabled aria-disabled="true"
                  className="mt-4 w-full py-2 px-3 text-sm bg-gray-100 text-gray-600 font-bold rounded-lg cursor-not-allowed"
                >Unlimited monthly plan, coming soon</button>
              </div>
            </div>

            {/* Gift cards */}
            <div className="border-t border-gray-200 pt-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Buy a Gift Card</h3>
              <p className="text-xs text-gray-400 mb-3">
                Give someone LinguistPro credit. A redemption code appears in your Wallet → Gift Cards after payment.
              </p>
              <p className="text-xs text-gray-500 mb-3">{paygGiftCardNote()}</p>
              <div className="flex gap-2">
                {PAYG_OPTIONS.map((opt) => (
                  <button
                    key={opt.amount}
                    type="button"
                    onClick={() => handlePayment('gift', opt.amount)}
                    className="flex-1 py-2 text-xs rounded-lg bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200 transition-colors"
                  >
                    Gift R{opt.amount}
                    <span className="block text-gray-400 text-[10px] mt-0.5">{opt.time}</span>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
